import { useState } from 'react';
import { Link } from 'react-router-dom';

import { getUserApi, updateUserApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr, formatDateTimeRelative, getInitials } from '@shared';
import { Gender } from '@shared/enums/domain.enum';
import { Role } from '@shared/enums/role.enum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Briefcase, Building, Edit, Loader2, Power, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';

import ConfirmModal from '../ui/ConfirmModal';

interface UserDetailDrawerProps {
  userId: number | null;
  onClose: () => void;
}

const ROLE_LABELS: Record<Role, string> = {
  [Role.SUPER_ADMIN]: 'Super Admin',
  [Role.ORGANIZATION_ADMIN]: 'Organisateur',
  [Role.CLUB_ADMIN]: 'Club',
  [Role.MEMBER]: 'Joueur',
};

const GENDER_LABELS: Record<Gender, string> = {
  [Gender.MALE]: 'Homme',
  [Gender.FEMALE]: 'Femme',
};

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='flex items-center justify-between gap-3 py-2'>
      <span className='text-sm text-slate-500'>{label}</span>
      <span className='text-right text-sm font-semibold text-slate-900'>{children}</span>
    </div>
  );
}

export default function UserDetailDrawer({ userId, onClose }: UserDetailDrawerProps) {
  const qc = useQueryClient();
  const [confirmToggle, setConfirmToggle] = useState(false);

  const open = userId !== null;

  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.users.detail(userId ?? 0),
    queryFn: () => getUserApi(userId as number),
    enabled: open,
  });

  const toggleMutation = useMutation({
    mutationFn: (disabled: boolean) => updateUserApi(userId as number, { disabled }),
    onSuccess: (_data, disabled) => {
      toast.success(disabled ? 'Compte désactivé' : 'Compte réactivé');
      setConfirmToggle(false);
      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(userId as number) });
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors de la mise à jour'),
  });

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />
      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col bg-white shadow-2xl transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className='flex items-start gap-3 border-b border-slate-200 p-5'>
          {user && (
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-sm font-bold text-white'>
              {getInitials(user.firstName, user.lastName)}
            </div>
          )}
          <div className='min-w-0 flex-1'>
            <h2 className='truncate text-lg font-bold text-slate-900'>
              {user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.username : 'Chargement…'}
            </h2>
            {user && <p className='truncate text-xs text-slate-400'>@{user.username}</p>}
          </div>
          {user && (
            <Link
              to={`/admin/users/${user.id}/edit`}
              className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-blue-600'
              title='Édition complète'
            >
              <Edit className='h-4 w-4' />
            </Link>
          )}
          <button
            onClick={onClose}
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100'
            aria-label='Fermer'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-5'>
          {isLoading || !user ? (
            <div className='flex h-40 items-center justify-center text-slate-400'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : (
            <div className='space-y-6'>
              {/* Compte */}
              <div className='rounded-xl border border-slate-200 p-4'>
                <h3 className='mb-2 text-sm font-bold text-slate-900'>Compte</h3>
                <InfoRow label='Rôle'>{ROLE_LABELS[user.role]}</InfoRow>
                <InfoRow label='Statut'>
                  {user.deletedAt ? (
                    <span className='rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700'>Supprimé</span>
                  ) : user.disabled ? (
                    <span className='rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700'>Désactivé</span>
                  ) : (
                    <span className='rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'>Actif</span>
                  )}
                </InfoRow>
                <InfoRow label='Email'>
                  <span className='inline-flex items-center gap-1'>
                    {user.email}
                    {user.emailVerified && <ShieldCheck className='h-3.5 w-3.5 text-emerald-500' />}
                  </span>
                </InfoRow>
                <InfoRow label='Téléphone'>{user.mobile ?? '—'}</InfoRow>
                <InfoRow label='MFA'>{user.mfaEnabled ? 'Activé' : 'Désactivé'}</InfoRow>
                <InfoRow label='Inscrit le'>{formatDateAbbreviatedFr(user.createdAt)}</InfoRow>
                <InfoRow label='Dernière connexion'>{formatDateTimeRelative(user.lastLogin)}</InfoRow>
              </div>

              {/* Données fédérales */}
              <div className='rounded-xl border border-slate-200 p-4'>
                <h3 className='mb-2 text-sm font-bold text-slate-900'>Données fédérales (FFPJP)</h3>
                <InfoRow label='N° licence'>{user.licenseNumber ?? '—'}</InfoRow>
                <InfoRow label='Naissance'>{user.birthDate ? formatDateAbbreviatedFr(user.birthDate) : '—'}</InfoRow>
                <InfoRow label='Sexe'>{user.gender ? GENDER_LABELS[user.gender] : '—'}</InfoRow>
                <InfoRow label='Classification'>{user.federalClassification ?? '—'}</InfoRow>
              </div>

              {/* Club */}
              {user.club && (
                <div className='rounded-xl border border-slate-200 p-4'>
                  <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
                    <Building className='h-4 w-4 text-slate-400' /> Club
                  </h3>
                  <div className='flex items-center gap-2 text-sm text-slate-700'>
                    <span className='font-semibold'>{user.club.name}</span>
                    {user.club.city && <span className='text-slate-400'>· {user.club.city}</span>}
                    {user.isClubAdmin && (
                      <span className='rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700'>Admin</span>
                    )}
                  </div>
                </div>
              )}

              {/* Organisations */}
              {user.organizationsAdmin.length > 0 && (
                <div className='rounded-xl border border-slate-200 p-4'>
                  <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
                    <Briefcase className='h-4 w-4 text-slate-400' /> Organisations gérées
                  </h3>
                  <div className='space-y-1'>
                    {user.organizationsAdmin.map(m => (
                      <div
                        key={m.organization.id}
                        className='flex items-center justify-between gap-2 text-sm text-slate-700'
                      >
                        <span className='truncate'>{m.organization.name}</span>
                        <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500'>{m.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {user && !user.deletedAt && user.role !== Role.SUPER_ADMIN && (
          <div className='border-t border-slate-200 p-4'>
            <button
              onClick={() => setConfirmToggle(true)}
              className={`flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                user.disabled
                  ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                  : 'border-orange-200 text-orange-700 hover:bg-orange-50'
              }`}
            >
              <Power className='h-4 w-4' />
              {user.disabled ? 'Réactiver le compte' : 'Désactiver le compte'}
            </button>
          </div>
        )}
      </aside>

      <ConfirmModal
        isOpen={confirmToggle}
        title={user?.disabled ? 'Réactiver le compte' : 'Désactiver le compte'}
        message={
          user?.disabled
            ? `Réactiver l'accès de ${user?.firstName ?? ''} ${user?.lastName ?? ''} ?`
            : `Désactiver l'accès de ${user?.firstName ?? ''} ${user?.lastName ?? ''} ? L'utilisateur ne pourra plus se connecter.`
        }
        confirmLabel={user?.disabled ? 'Réactiver' : 'Désactiver'}
        variant={user?.disabled ? 'primary' : 'danger'}
        isLoading={toggleMutation.isPending}
        onConfirm={() => toggleMutation.mutate(!user?.disabled)}
        onCancel={() => setConfirmToggle(false)}
      />
    </>
  );
}
